/**
 * One-shot "a logout is in progress" marker, shared between the logout trigger
 * and the login route.
 *
 * Why this exists: logout does `removeUser()` (which flips `isAuthenticated` to
 * false and re-renders the app) and THEN navigates to Cognito's `/logout`. The
 * re-render mounts the auto-redirecting LoginPage, whose effect fires
 * `signinRedirect()` and navigates to `/authorize` — cancelling the in-flight
 * `/logout` request before Cognito can clear its session. The user is then
 * silently logged back in.
 *
 * Setting this marker before logout lets LoginPage skip its auto-redirect, so
 * the `/logout` navigation wins and the session is actually cleared. It also
 * drives the post-logout "you've been signed out" screen.
 *
 * sessionStorage (not a module variable) because logout is a full-page
 * navigation away to Cognito and back — the flag must survive that round-trip.
 * It is scoped to the tab and consumed on read, so it never leaks into a normal
 * fresh visit.
 */
const LOGGING_OUT_KEY = 'pf:loggingOut'

/** Mark that a user-initiated logout has started. Call before signoutRedirect(). */
export const markLoggingOut = (): void => {
  try {
    sessionStorage.setItem(LOGGING_OUT_KEY, '1')
  } catch {
    // sessionStorage can throw in private-mode / sandboxed contexts; a missing
    // marker only degrades to the old auto-redirect behavior, so swallow it.
  }
}

/**
 * Read-and-clear the logout marker. Returns true exactly once per logout.
 * Subsequent calls (and fresh visits) return false.
 */
export const consumeLoggingOut = (): boolean => {
  try {
    if (sessionStorage.getItem(LOGGING_OUT_KEY) === '1') {
      sessionStorage.removeItem(LOGGING_OUT_KEY)
      return true
    }
  } catch {
    // See markLoggingOut.
  }
  return false
}
